// components/OcrUploadModal.jsx
import React, { useState } from 'react';
import { Button, Icon } from './ui';

export default function OcrUploadModal({ show, onClose, onOcrComplete }) {
  const [file, setFile] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    // Vérifier le type
    const validTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
    if (!validTypes.includes(selectedFile.type)) {
      setError('Type de fichier non supporté. Utilisez PDF ou images (JPG, PNG, GIF).');
      return;
    }

    // Vérifier la taille (20MB max)
    if (selectedFile.size > 20 * 1024 * 1024) {
      setError('Fichier trop volumineux (max 20MB).');
      return;
    }

    setFile(selectedFile);
    setError('');

    // Créer un preview pour les images
    if (selectedFile.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => setPreview(e.target.result);
      reader.readAsDataURL(selectedFile);
    } else {
      setPreview(null);
    }
  };

  const handleProcess = async () => {
    if (!file) {
      setError('Veuillez sélectionner un fichier.');
      return;
    }

    setProcessing(true);
    setError('');

    try {
      const result = await onOcrComplete(file);
      
      // Fermer le modal et passer les données au parent
      onClose();
    } catch (e) {
      const msg = e?.response?.data?.error || e?.message || 'Erreur lors du traitement OCR.';
      setError(msg);
    } finally {
      setProcessing(false);
    }
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold">📄 Créer depuis une facture</h2>
              <p className="text-blue-100 text-sm mt-1">
                Uploadez une facture PDF ou image, on extrait automatiquement les données
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 text-2xl"
              disabled={processing}
            >
              ×
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="p-6 space-y-6">
          {/* Zone d'upload */}
          <div>
            <label className="block text-sm font-medium mb-3 text-gray-700">
              Sélectionnez votre facture
            </label>
            
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-500 transition-colors">
              <input
                type="file"
                accept="application/pdf,image/jpeg,image/jpg,image/png,image/gif"
                onChange={handleFileChange}
                className="hidden"
                id="ocr-file-input"
                disabled={processing}
              />
              
              <label
                htmlFor="ocr-file-input"
                className="cursor-pointer flex flex-col items-center"
              >
                <svg
                  className="w-16 h-16 text-gray-400 mb-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                  />
                </svg>
                
                {file ? (
                  <div>
                    <p className="text-lg font-semibold text-gray-800">{file.name}</p>
                    <p className="text-sm text-gray-500 mt-1">
                      {(file.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                    <p className="text-blue-600 text-sm mt-2">Cliquez pour changer</p>
                  </div>
                ) : (
                  <div>
                    <p className="text-lg font-semibold text-gray-700">
                      Cliquez pour sélectionner un fichier
                    </p>
                    <p className="text-sm text-gray-500 mt-2">
                      PDF, JPG, PNG, GIF (max 20MB)
                    </p>
                  </div>
                )}
              </label>
            </div>
          </div>

          {/* Preview image */}
          {preview && (
            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700">Aperçu</label>
              <div className="border rounded-lg overflow-hidden">
                <img src={preview} alt="Preview" className="w-full h-auto max-h-64 object-contain" />
              </div>
            </div>
          )}

          {/* Erreur */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start">
                <svg
                  className="w-5 h-5 text-red-600 mr-2 mt-0.5"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
                <div>
                  <div className="text-sm font-semibold text-red-800">Erreur</div>
                  <div className="text-sm text-red-700 mt-1">{error}</div>
                </div>
              </div>
            </div>
          )}

          {/* Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start">
              <svg
                className="w-5 h-5 text-blue-600 mr-2 mt-0.5"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clipRule="evenodd"
                />
              </svg>
              <div className="text-sm text-blue-800">
                <strong>Comment ça marche ?</strong>
                <ul className="list-disc list-inside mt-2 space-y-1">
                  <li>Nous extrayons automatiquement le texte de votre facture</li>
                  <li>Les champs du formulaire seront pré-remplis</li>
                  <li>Vous pourrez vérifier et corriger les données avant de sauvegarder</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 flex justify-end gap-3 rounded-b-xl">
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={processing}
          >
            Annuler
          </Button>
          <Button
            type="button"
            onClick={handleProcess}
            disabled={!file || processing}
          >
            {processing ? (
              <>
                <svg
                  className="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Traitement en cours...
              </>
            ) : (
              <>
                <Icon name="IconWand" className="mr-2" />
                Extraire les données
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}